/**
 * Broker Disclosure — nationwide advertising compliance block.
 *
 * State real-estate commissions treat every consumer-facing surface (the
 * client portal, portal invite emails, share pages, and client-facing
 * message footers) as REAL-ESTATE ADVERTISING. Those rules require the
 * employing brokerage to be identified "clearly and prominently" — at
 * least as prominently as the agent themselves. A muted footer or a
 * smaller font on the brokerage line is the single most common cause of
 * advertising violations, so this component:
 *
 *   • Renders the brokerage name at the SAME font-size and font-weight
 *     as the agent's name in the `full` variant.
 *   • Uses an equal-width two-column layout so neither identity dominates.
 *   • Fits into the viewport without needing scroll — kept compact enough
 *     that a mobile portal home page still shows the whole block above
 *     the fold below the transaction card.
 *   • Trusts the API payload — the server has already applied the per-
 *     state visibility rules and stripped fields the state doesn't need.
 *
 * The component intentionally does NOT claim iKEY guarantees legal
 * compliance. The agent and their designated broker are responsible for
 * the accuracy of the identity data they enter.
 */
import { House } from "lucide-react";

/**
 * Join non-empty strings with a middot separator — used all over the
 * compact/full renders so we never end up with a trailing " · " on a
 * partially-filled account.
 */
const joinDots = (parts) =>
  parts.filter((p) => typeof p === "string" && p.trim().length > 0).join(" · ");

function agentLine(a, rules) {
  const bits = [a.full_name, a.title].filter(Boolean);
  if (a.license_number && rules?.show_agent_license !== false) {
    bits.push(`Lic. #${a.license_number}${a.license_state ? " " + a.license_state : ""}`);
  }
  return joinDots(bits);
}

function brokerageLine(b, rules) {
  const bits = [b.legal_name, b.dba].filter(Boolean);
  if (b.license_number && rules?.show_brokerage_license !== false) {
    bits.push(`Lic. #${b.license_number}`);
  }
  return joinDots(bits);
}

function officeAddress(b, rules) {
  if (rules?.show_office_address === false) return "";
  const street = [b.street, b.suite].filter(Boolean).join(" ");
  const city = [b.city, [b.state, b.zip].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");
  return [street, city].filter(Boolean).join(" · ");
}

function officePhone(b, rules) {
  if (!b.phone || rules?.show_phone_numbers === false) return "";
  return b.phone;
}

function DisclaimerLines({ disclaimers, className = "" }) {
  const lines = [];
  if (disclaimers.independently_owned) lines.push("Each office independently owned and operated.");
  if (disclaimers.equal_housing_notice) lines.push("Equal Housing Opportunity.");
  if (disclaimers.state_required) lines.push(disclaimers.state_required);
  if (disclaimers.custom) lines.push(disclaimers.custom);
  if (lines.length === 0) return null;
  return (
    <p className={`text-[11px] leading-[16px] text-slate-500 ${className}`} data-testid="disclosure-disclaimers">
      {lines.join(" ")}
    </p>
  );
}

/**
 * Full variant — used on every client-portal page.
 * Two equal-width columns. Agent left, brokerage right, hairline between.
 * Below the columns: divider · disclaimers · equal-housing notice.
 */
export function BrokerDisclosureFull({ disclosure, className = "" }) {
  if (!disclosure) return null;
  const { agent = {}, brokerage = {}, disclaimers = {}, rules = {} } = disclosure;

  // Guard against a totally empty disclosure — nothing to render.
  const anythingToShow =
    agent.full_name || brokerage.legal_name || brokerage.street || disclaimers.independently_owned || disclaimers.equal_housing_notice;
  if (!anythingToShow) return null;

  const agentBits = agentLine(agent, rules);
  const brokerageBits = brokerageLine(brokerage, rules);
  const addr = officeAddress(brokerage, rules);
  const phone = officePhone(brokerage, rules);
  const agentContact = joinDots([
    rules?.show_phone_numbers !== false ? agent.phone : null,
    agent.email,
  ]);

  return (
    <section
      className={`rounded-lg border border-slate-200 bg-white p-4 sm:p-5 ${className}`}
      aria-label="Broker disclosure"
      data-testid="broker-disclosure-full"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">
        Broker disclosure
      </p>

      <div className="grid grid-cols-2 gap-4 sm:gap-6 divide-x divide-slate-200">
        <div className="pr-3 sm:pr-4 min-w-0">
          <p
            className="text-[13px] font-semibold text-slate-900 leading-snug break-words"
            data-testid="disclosure-agent-name"
          >
            {agentBits || agent.full_name || "—"}
          </p>
          {agentContact && (
            <p className="mt-1 text-[12px] text-slate-600 leading-snug break-words">{agentContact}</p>
          )}
        </div>

        <div className="pl-3 sm:pl-4 min-w-0">
          {/* CRITICAL: brokerage name renders at THE SAME font-size + font-weight
              as the agent's name (both `text-[13px] font-semibold text-slate-900`).
              State advertising rules mandate that the employing broker appear at
              least as prominently as the agent — do not downgrade the styling. */}
          <p
            className="text-[13px] font-semibold text-slate-900 leading-snug break-words"
            data-testid="disclosure-brokerage-name"
          >
            {brokerageBits || brokerage.legal_name || "—"}
          </p>
          {(addr || phone) && (
            <p className="mt-1 text-[12px] text-slate-600 leading-snug break-words">
              {joinDots([addr, phone])}
            </p>
          )}
        </div>
      </div>

      {(disclaimers.independently_owned || disclaimers.equal_housing_notice || disclaimers.custom || disclaimers.state_required) && (
        <>
          <div className="mt-4 border-t border-slate-200" />
          <div className="mt-3 flex items-start gap-2">
            {disclaimers.equal_housing_notice && (
              <House
                className="h-3.5 w-3.5 mt-[2px] text-slate-400 shrink-0"
                aria-hidden="true"
              />
            )}
            <DisclaimerLines disclaimers={disclaimers} />
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Compact variant — used in tight spaces (client-facing message footer,
 * public share pages, invite email HTML mirrored here for the in-app
 * "preview email" toast). Four stacked lines, brokerage still same
 * weight as the agent name.
 */
export function BrokerDisclosureCompact({ disclosure, className = "" }) {
  if (!disclosure) return null;
  const { agent = {}, brokerage = {}, disclaimers = {}, rules = {} } = disclosure;

  const agentBits = agentLine(agent, rules);
  const brokerageBits = brokerageLine(brokerage, rules);
  const addressAndPhone = joinDots([
    officeAddress(brokerage, rules),
    officePhone(brokerage, rules),
  ]);

  const anythingToShow = agentBits || brokerageBits || addressAndPhone;
  if (!anythingToShow) return null;

  return (
    <div
      className={`rounded-md border border-slate-200 bg-white/60 backdrop-blur-sm px-3 py-2 ${className}`}
      aria-label="Broker disclosure"
      data-testid="broker-disclosure-compact"
    >
      {agentBits && (
        <p className="text-[12px] font-semibold text-slate-900 leading-snug" data-testid="disclosure-compact-agent">{agentBits}</p>
      )}
      {brokerageBits && (
        <p className="text-[12px] font-semibold text-slate-900 leading-snug" data-testid="disclosure-compact-brokerage">{brokerageBits}</p>
      )}
      {addressAndPhone && (
        <p className="text-[11px] text-slate-600 leading-snug">{addressAndPhone}</p>
      )}
      <DisclaimerLines disclaimers={disclaimers} className="mt-1" />
    </div>
  );
}

// Default export: pick a variant with `variant="full" | "compact"`.
export default function BrokerDisclosure({ disclosure, variant = "full", className = "" }) {
  return variant === "compact"
    ? <BrokerDisclosureCompact disclosure={disclosure} className={className} />
    : <BrokerDisclosureFull disclosure={disclosure} className={className} />;
}
